import React, {useEffect, useMemo} from "react";
import { IoClose } from "react-icons/io5";

/**
 * Component for displaying an uploaded video with a comment box and a close button.
 * Videos do not have caption overlays like pictures do.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.video - The video object containing the file and comment.
 * @param {Function} props.onCommentChange - The function to call when the comment changes.
 * @param {Function} props.onClose - The function to call when the close button is clicked.
 */
const VideoUpload = ({ video, onCommentChange, onClose }) => {
  // Memoize the object URL to prevent video reload on re-renders
  const videoUrl = useMemo(() => URL.createObjectURL(video.file), [video.file]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  const handleCommentChange = (e) => {
    onCommentChange(e.target.value);
  };

  return (
    <div className="mb-4 flex flex-col caret-transparent text-gray-700 py-2 p-1 shadow-gray-300 rounded-lg shadow-md relative">
      <button
        onClick={onClose}
        className="absolute bg-gray-400 hover:scale-125 shadow-md shadow-gray-700 opacity-75 rounded-3xl top-2 right-2 p-2 transition-colors ease-in-out duration-200 z-10"
      >
        <IoClose className="text-xl text-black" />
      </button>
      <video
        src={videoUrl}
        controls
        className="w-full col-span-2 md:col-span-1 object-cover rounded-t-md"
      />
      <div className="mt-2">
        <p className="text-sm text-gray-600 mb-2 font-semibold">
          {video.file.name}
        </p>
        <label
          className="block text-gray-700 text-sm font-bold mb-2"
          htmlFor="comment"
        >
          Comments:
        </label>
        <textarea
          value={video.comment}
          onChange={handleCommentChange}
          placeholder="Add comments..."
          className="mt-auto shadow col-span-2 md:col-span-3 appearance-none caret-black border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
      </div>
    </div>
  );
};

export default VideoUpload;
